import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Droplets, Sun, Wind, Sprout, Calendar, AlertCircle, Cloud, TrendingUp, Camera, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [plantings, setPlantings] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weatherAdvice, setWeatherAdvice] = useState(null);
  const [sowingRecs, setSowingRecs] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tasksRes, plantingsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/tasks`, { withCredentials: true }),
          axios.get(`${API}/plantings`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setTasks(tasksRes.data.filter(t => !t.completed).slice(0, 5));
        setPlantings(plantingsRes.data.filter(p => p.stage !== 'harvest').slice(0, 4));

        // Fetch weather and predictions (may fail if no location)
        try {
          const weatherRes = await axios.get(`${API}/weather`, { withCredentials: true });
          if (!weatherRes.data.error) setWeather(weatherRes.data);
        } catch (e) { console.log('Weather not available'); }

        try {
          const adviceRes = await axios.get(`${API}/weather/advice`, { withCredentials: true });
          setWeatherAdvice(adviceRes.data);
        } catch (e) { console.log('Weather advice not available'); }

        try {
          const sowRes = await axios.get(`${API}/recommendations/sow-now`, { withCredentials: true });
          setSowingRecs(sowRes.data.recommendations || []);
        } catch (e) { console.log('Sowing recs not available'); }

        try {
          const predRes = await axios.get(`${API}/harvests/predictions`, { withCredentials: true });
          setPredictions((predRes.data.predictions || []).filter(p => p.readiness === 'ready_now' || p.readiness === 'harvest_soon').slice(0, 3));
        } catch (e) { console.log('Predictions not available'); }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const currentTemp = weather?.current?.temperature_2m;
  const maxTemp = weather?.daily?.temperature_2m_max?.[0];
  const minTemp = weather?.daily?.temperature_2m_min?.[0];
  const precipToday = weather?.daily?.precipitation_sum?.[0];

  return (
    <Layout user={user}>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {getGreeting()}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-base text-[#5C665C] mt-2 leading-relaxed">
            Here&apos;s what&apos;s happening in your garden today
          </p>
        </div>

        {/* Weather Risk Alerts (if any) */}
        {weather?.risks && weather.risks.length > 0 && (
          <div className="space-y-2" data-testid="risk-alerts">
            {weather.risks.slice(0, 2).map((risk, idx) => (
              <div key={idx} className={`p-4 rounded-2xl border flex items-start gap-3 ${
                risk.severity === 'high' ? 'bg-[#E2725B]/10 border-[#E2725B]/30' :
                risk.severity === 'medium' ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' :
                'bg-[#8AB4F8]/10 border-[#8AB4F8]/30'
              }`}>
                <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  risk.severity === 'high' ? 'text-[#E2725B]' :
                  risk.severity === 'medium' ? 'text-[#D4AF37]' :
                  'text-[#8AB4F8]'
                }`} />
                <p className="text-sm text-[#2D241E] leading-relaxed">{risk.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/my-crops" className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm card-hover" data-testid="stat-active-crops">
            <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 flex items-center justify-center mb-2">
              <Sprout className="w-5 h-5 text-[#1A4331]" />
            </div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.active_plantings || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Active Crops</p>
          </Link>

          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="stat-tasks">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{tasks?.length || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Today&apos;s Tasks</p>
          </div>

          <Link to="/harvests" className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm card-hover" data-testid="stat-harvests">
            <div className="w-10 h-10 rounded-full bg-[#E2725B]/10 flex items-center justify-center mb-2">
              <TrendingUp className="w-5 h-5 text-[#E2725B]" />
            </div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{predictions.length}</p>
            <p className="text-sm text-[#5C665C] mt-1">Ready Soon</p>
          </Link>

          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="stat-garden-health">
            <div className="w-10 h-10 rounded-full bg-[#B4D330]/10 flex items-center justify-center mb-2">
              <Sun className="w-5 h-5 text-[#B4D330]" />
            </div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.garden_health || 85}%</p>
            <p className="text-sm text-[#5C665C] mt-1">Garden Health</p>
          </div>
        </div>

        {/* Weather + Watering Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/weather" className="bg-gradient-to-br from-[#8AB4F8]/20 to-[#8AB4F8]/5 rounded-3xl p-6 border border-[#8AB4F8]/20 card-hover" data-testid="weather-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Today&apos;s Weather</h3>
              <Cloud className="w-6 h-6 text-[#8AB4F8]" />
            </div>
            {weather ? (
              <>
                <p className="text-5xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {Math.round(currentTemp)}°C
                </p>
                <div className="flex items-center gap-4 mt-3 text-sm text-[#5C665C]">
                  <span>H: {Math.round(maxTemp)}°</span>
                  <span>L: {Math.round(minTemp)}°</span>
                  {precipToday > 0 && <span>💧 {precipToday.toFixed(1)}mm</span>}
                </div>
                {weatherAdvice?.advice?.general && (
                  <p className="text-sm text-[#5C665C] mt-3 leading-relaxed">{weatherAdvice.advice.general}</p>
                )}
              </>
            ) : (
              <div>
                <p className="text-[#5C665C] mb-3">Set your location to enable weather intelligence</p>
                <Link to="/settings" className="text-sm font-semibold text-[#1A4331] hover:text-[#255C44]">
                  Add location →
                </Link>
              </div>
            )}
          </Link>

          <Link to="/watering" className="bg-gradient-to-br from-[#8EAA97]/20 to-[#8EAA97]/5 rounded-3xl p-6 border border-[#8EAA97]/20 card-hover" data-testid="watering-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#2D241E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Watering Advice</h3>
              <Droplets className="w-6 h-6 text-[#8AB4F8]" />
            </div>
            <div className="space-y-3">
              <p className="text-[#2D241E] font-semibold leading-relaxed">
                {weatherAdvice?.advice?.watering || 'Set location for personalized watering advice'}
              </p>
              <p className="text-sm font-semibold text-[#1A4331]">View watering schedule →</p>
            </div>
          </Link>
        </div>

        {/* Today's Tasks */}
        <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="tasks-section">
          <h3 className="text-2xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Today&apos;s Tasks</h3>
          {tasks.length === 0 ? (
            <p className="text-[#5C665C]">No pending tasks. Your garden is all caught up! 🌱</p>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.task_id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F0F2EB] transition-colors" data-testid={`task-${task.task_id}`}>
                  <input type="checkbox" className="w-5 h-5 rounded border-[#8EAA97]" data-testid={`task-checkbox-${task.task_id}`} />
                  <div className="flex-1">
                    <p className="font-medium text-[#2D241E]">{task.title}</p>
                    {task.description && <p className="text-sm text-[#5C665C]">{task.description}</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    task.priority === 'high' ? 'bg-[#E2725B]/10 text-[#E2725B]' :
                    task.priority === 'medium' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                    'bg-[#8EAA97]/10 text-[#8EAA97]'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sow This Week */}
        {sowingRecs.length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="sowing-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Sow This Week</h3>
                <p className="text-sm text-[#5C665C] mt-1">Perfect timing for these crops in your area</p>
              </div>
              <Link to="/crops" className="text-sm font-semibold text-[#1A4331] hover:text-[#255C44]">View all →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {sowingRecs.slice(0, 8).map((crop) => (
                <Link key={crop.crop_id} to={`/crops/${crop.crop_id}`} data-testid={`sow-rec-${crop.crop_id}`} className="bg-[#B4D330]/10 hover:bg-[#B4D330]/20 transition-colors p-4 rounded-2xl">
                  <p className="font-semibold text-[#1A4331]">{crop.name}</p>
                  <p className="text-xs text-[#5C665C] mt-1">{crop.days_to_harvest}d to harvest</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active Crops Preview */}
        {plantings.length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="active-crops-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Active Crops</h3>
              <Link to="/my-crops" className="text-sm font-semibold text-[#1A4331] hover:text-[#255C44]" data-testid="view-all-crops-link">View all →</Link>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {plantings.map((planting) => (
                <div key={planting.planting_id} className="flex items-center gap-4 p-4 rounded-2xl bg-[#F0F2EB]" data-testid={`planting-${planting.planting_id}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center stage-${planting.stage}`}>
                    <Sprout className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#2D241E]">{planting.crop_name}</p>
                    <p className="text-sm text-[#5C665C] capitalize">{planting.stage.replace('_', ' ')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/coach" data-testid="quick-action-coach" className="bg-gradient-to-br from-[#8EAA97] to-[#1A4331] rounded-3xl p-6 text-white card-hover">
            <MessageCircle className="w-8 h-8 mb-3" />
            <h4 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Ask AI Coach</h4>
            <p className="text-white/90 text-sm">Get expert gardening advice instantly</p>
          </Link>
          <Link to="/scanner" data-testid="quick-action-scanner" className="bg-gradient-to-br from-[#E2725B] to-[#D4AF37] rounded-3xl p-6 text-white card-hover">
            <Camera className="w-8 h-8 mb-3" />
            <h4 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Scan Plant Issue</h4>
            <p className="text-white/90 text-sm">Identify pests and diseases with AI</p>
          </Link>
          <Link to="/planner" data-testid="quick-action-planner" className="bg-gradient-to-br from-[#B4D330] to-[#8EAA97] rounded-3xl p-6 text-white card-hover">
            <Sprout className="w-8 h-8 mb-3" />
            <h4 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Plan Garden</h4>
            <p className="text-white/90 text-sm">Design your beds and crop layout</p>
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
